import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  Button,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useFormContext } from 'react-hook-form';

export interface ContractAgreementCheckboxProps {
  /**
   * URL du PDF des CGV. Si fournie, le lien "Lire les conditions" ouvre le
   * PDF dans un nouvel onglet. Sinon, fallback sur le HTML `agreementTerms`
   * affiché dans un modal.
   */
  pdfUrl?: string | null;
  /**
   * HTML brut renvoyé par Mindbody (`MindbodyContract.AgreementTerms`).
   * Utilisé en fallback si `pdfUrl` est vide. Peut être vide aussi — dans ce
   * cas le modal affiche un message générique (l'admin doit configurer le PDF).
   */
  agreementTerms?: string | null;
}

/**
 * Checkbox CGV obligatoire pour la souscription d'un contract. Cochée =
 * `AcceptedTerms === true`. Validation du form gérée par le `register`
 * `validate` ci-dessous.
 */
export function ContractAgreementCheckbox({ pdfUrl, agreementTerms }: ContractAgreementCheckboxProps) {
  const { register, formState } = useFormContext<{ AcceptedTerms?: boolean }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const error = formState.errors.AcceptedTerms;
  const hasPdf = !!pdfUrl && pdfUrl.length > 0;

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (hasPdf) {
      // window.open géré par le href + target=_blank natif
      return;
    }
    e.preventDefault();
    onOpen();
  };

  return (
    <FormControl isInvalid={!!error} mt={2}>
      <Checkbox
        {...register('AcceptedTerms', {
          validate: (v) => v === true || 'Vous devez accepter les conditions du contrat pour continuer.',
        })}
        colorScheme="orange"
        alignItems="flex-start"
      >
        <Text as="span" fontSize="sm" lineHeight="1.4">
          En confirmant mon paiement, j'accepte les{' '}
          <Link
            href={hasPdf ? (pdfUrl as string) : '#'}
            target={hasPdf ? '_blank' : undefined}
            rel={hasPdf ? 'noopener noreferrer' : undefined}
            color="orange.600"
            textDecoration="underline"
            onClick={onLinkClick}
          >
            CGV Monday Sports Club{hasPdf ? <ExternalLinkIcon mx="2px" boxSize="0.8em" /> : null}
          </Link>
        </Text>
      </Checkbox>
      <FormErrorMessage>{error?.message}</FormErrorMessage>

      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Conditions générales du contrat</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {agreementTerms && agreementTerms.trim().length > 0 ? (
              <Box
                dangerouslySetInnerHTML={{ __html: agreementTerms }}
                fontSize="sm"
                lineHeight="1.6"
                sx={{ ul: { paddingLeft: '1em', marginLeft: '1em' } }}
              />
            ) : (
              <Text fontSize="sm" color="gray.600">
                Aucune condition de contrat n'est disponible pour cet abonnement. Merci de contacter le studio pour
                obtenir le détail des conditions avant souscription.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Fermer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
}
